import { KeyboardEvent, useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Reaction, ReactionType } from '../../../../generated/graphql';

type PostFooterProps = {
  reactions: Reaction[];
  onCommentCountClick: () => void;
  onAddCommentClick: () => void;
  isExpanded?: boolean;
};

export const PostFooter = ({
  reactions,
  onCommentCountClick,
  onAddCommentClick,
  isExpanded = false,
}: PostFooterProps) => {
  const commentsCount = useMemo(() => {
    const comments = reactions.find((r) => r.type === ReactionType.Comment);
    return comments?.count ?? 0;
  }, [reactions]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onCommentCountClick();
      }
    },
    [onCommentCountClick],
  );

  return (
    <Box className="flex flex-row justify-between items-center">
      <span
        role="button"
        tabIndex={0}
        onClick={onCommentCountClick}
        onKeyDown={handleKeyDown}
        style={{
          fontWeight: 800,
          fontSize: '0.6rem',
          lineHeight: '0.875rem',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {commentsCount === 1 ? '1 comment' : `${commentsCount} comments`}
      </span>
      <Button
        variant="text"
        onClick={onAddCommentClick}
        sx={{
          display: 'flex',
          padding: '8px 16px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          textTransform: 'none',
        }}
      >
        {!isExpanded && (
          <FontAwesomeIcon
            icon={faPlus}
            style={{
              color: 'var(--sys-primary-P-80, #5F1ECC)',
              textAlign: 'center',
              fontFamily: '"Font Awesome 6 Pro"',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: '18px',
            }}
          />
        )}
        <span
          style={{
            color: 'var(--sys-primary-P-80, #5F1ECC)',
            fontFamily: 'Visuelt, sans-serif',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
          }}
        >
          {isExpanded ? 'Hide comments' : 'Comment'}
        </span>
      </Button>
    </Box>
  );
};
